import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user.store';

type CommentLike = { author?: { id?: number }; is_read?: boolean };

/**
 * Unread-comments indicator backed by the server's per-user read state.
 *
 * A comment counts as unread when its author is not the current user and the
 * backend reports `is_read: false` for them. Opening the Comments tab marks the
 * entity's comments read via the provided `markRead` action.
 */
export function useUnreadComments() {
  const hasUnread = ref(false);
  const { currentUser } = storeToRefs(useUserStore());

  let entityId: number | null = null;
  let markReadFn: ((id: number) => Promise<any>) | null = null;

  async function check(
    id: number | null | undefined,
    fetcher: (id: number) => Promise<any>,
    markRead: (id: number) => Promise<any>
  ) {
    if (!id) return;
    entityId = id;
    markReadFn = markRead;
    try {
      const res = await fetcher(id);
      const list: CommentLike[] = res?.results ?? res ?? [];
      hasUnread.value = list.some(
        comment => !comment.is_read && comment.author?.id !== currentUser.value?.id
      );
    } catch {
      hasUnread.value = false;
    }
  }

  async function markSeen() {
    if (!hasUnread.value || !markReadFn || !entityId) return;
    hasUnread.value = false;
    try {
      await markReadFn(entityId);
    } catch {
      /* UI already cleared; a later fetch will reconcile if this failed */
    }
  }

  return { hasUnread, check, markSeen };
}
